import { createModal } from './modal.js';
import { post, get, del } from './persist.js';
import { openAddressForm } from './address-form.js';
import { CLOSE_SM, iconNode } from './icons.js';

// Recipients lists don't have a list-level id — each entry references an
// address by addressId, and the list itself is scoped by the URL (one list
// per delivery group). For pre-integration testing we stash the enriched
// recipients (address fields + quantity) keyed by the .checkout__action
// element (stable across the partial-vs-full state transitions that swap
// which inner element is the trigger), so re-opening the dialog repopulates
// the cards without round-tripping.
const dummyRecipients = new WeakMap();

function dummyKey(opener) {
  return opener?.closest('.checkout__action') || opener || null;
}

export function initRecipients() {
  document.querySelectorAll('[data-recipients]').forEach(wrap => {
    createInstance(wrap);
  });
}

function createInstance(wrap) {
  const dialog          = wrap.querySelector('.rcp-modal');
  const emptyEl         = dialog.querySelector('.rcp-empty');
  const allocEl         = dialog.querySelector('.rcp-alloc');
  const allocTextEl     = allocEl?.querySelector('.rcp-alloc__text');
  const listEl          = dialog.querySelector('.rcp-list');
  const addBtn          = dialog.querySelector('.rcp-add-btn');
  const confirmBtn      = dialog.querySelector('.modal__confirm');
  const errorEl         = dialog.querySelector('.modal__error');

  // Editing a recipient's address (a card click, below) goes straight to
  // the shared address editor and saves to the address book's own
  // canonical endpoint — not the recipients upsert endpoint — since it's
  // modifying the underlying address record, not this list.
  const addressSaveUrl = document.querySelector('[data-address-book]')?.dataset.addressSaveUrl || '';
  const editorDialogId = document.querySelector('[data-address-book]')?.dataset.editorDialog || 'address-editor';

  let activeOpener        = null;
  let recipients          = [];
  // Snapshot of what the server actually has right now — updated only by
  // a successful Confirm POST or a successful immediate delete, never by
  // local-only edits (quantity changes, new adds, address edits). This is
  // what dispatchRecipientsUpdate sends to the checkout page trigger, so
  // closing without pressing Confirm never reports unsaved changes —
  // except deletes, which are already real by the time they land here.
  let persistedRecipients = [];
  let capacity            = 0;
  let unitSingular        = 'item';
  let unitPlural          = 'items';
  let isSaving            = false;
  let confirmingRemoveId  = null;
  // Set while the delete-recipient call for confirmingRemoveId is in
  // flight — blocks a second click on Cancel/Remove and on Confirm until
  // it resolves, so the list on screen never gets ahead of the server.
  let isRemoving          = false;

  function clearError() {
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.hidden = true;
  }

  function showError(msg) {
    if (!errorEl) return;
    errorEl.textContent = msg;
    errorEl.hidden = false;
  }

  function totalAllocated() {
    return recipients.reduce((sum, r) => sum + r.quantity, 0);
  }

  function render() {
    const total = totalAllocated();
    const atCapacity = total >= capacity;

    if (recipients.length === 0) {
      emptyEl.hidden    = false;
      allocEl.hidden    = true;
      confirmBtn.hidden = true;
    } else {
      emptyEl.hidden    = true;
      allocEl.hidden    = false;
      confirmBtn.hidden = false;
      confirmBtn.disabled = isRemoving;
      allocTextEl.innerHTML =
        `<span>${total}</span> of <span>${capacity}</span> ${unitPlural} allocated to <span>${recipients.length}</span> ${pluralize(recipients.length, 'recipient', 'recipients')}`;
    }

    if (addBtn) addBtn.disabled = atCapacity;

    listEl.replaceChildren();
    recipients.forEach(r => listEl.appendChild(renderCard(r, atCapacity)));
  }

  function renderCard(recipient, atCapacity) {
    const isConfirming = String(recipient.id) === String(confirmingRemoveId);

    const li = document.createElement('li');
    li.className = 'rcp-card';
    if (isConfirming) li.classList.add('is-confirming');
    li.dataset.id = recipient.id;

    // ── Top row: address text + remove (X) button. Clicking anywhere on
    // the top row (except the remove X) opens the address editor directly
    // — bypassing the address book — prefilled with this recipient's
    // current address and its id, so Confirm updates that address-book
    // record in place rather than picking a different saved address.
    // data-book-notice tells the editor to show the "this updates your
    // address book" message, since that isn't obvious from this entry
    // point the way it is when editing from inside the book. None of this
    // applies while confirming removal — the X is gone and editing a
    // recipient you're about to remove isn't useful.
    const top = document.createElement('div');
    top.className = 'rcp-card__top';

    const info = document.createElement('p');
    info.className = 'rcp-card__info';

    const nameEl = document.createElement('span');
    nameEl.className = 'rcp-card__info-name';
    nameEl.textContent = [recipient.firstName, recipient.lastName].filter(Boolean).join(' ');

    const addressEl = document.createElement('span');
    addressEl.className = 'rcp-card__info-address';
    addressEl.textContent = formatAddressLine(recipient);

    info.append(nameEl, addressEl);
    top.append(info);

    if (!isConfirming) {
      if (recipient.addressId) top.dataset.savedId = recipient.addressId;
      top.dataset.saveUrl = addressSaveUrl;
      top.dataset.bookNotice = '';

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'rcp-card__remove-btn';
      removeBtn.setAttribute('aria-label', 'Remove recipient');
      // Same icon and wrapper as the Pug `+icon('close-sm')` mixin emits —
      // so the icon-system colour + hover stroke rules apply uniformly.
      removeBtn.appendChild(iconNode(CLOSE_SM));
      removeBtn.addEventListener('click', () => {
        confirmingRemoveId = recipient.id;
        clearError();
        render();
      });
      top.append(removeBtn);

      top.addEventListener('click', e => {
        // Don't hijack the X — its own listener handles entering confirm mode.
        if (e.target.closest('.rcp-card__remove-btn')) return;
        openAddressForm(editorDialogId, {
          firstName:    recipient.firstName,
          lastName:     recipient.lastName,
          addressLine1: recipient.addressLine1,
          addressLine2: recipient.addressLine2,
          addressLine3: recipient.addressLine3,
          city:         recipient.city,
          postcode:     recipient.postcode,
          country:      recipient.country,
        }, top);
      });
    }

    // ── Bottom row: either the quantity stepper, or the Cancel/Remove confirm
    const bottom = document.createElement('div');
    bottom.className = 'rcp-card__bottom';

    if (isConfirming) {
      bottom.append(renderConfirmButtons(recipient));
    } else {
      bottom.append(renderQuantityStepper(recipient, atCapacity));
    }

    li.append(top, bottom);
    return li;
  }

  function renderQuantityStepper(recipient, atCapacity) {
    const qty = document.createElement('div');
    qty.className = 'rcp-card__qty';

    const minusBtn = document.createElement('button');
    minusBtn.type = 'button';
    minusBtn.className = 'rcp-card__qty-btn';
    minusBtn.setAttribute('aria-label', 'Decrease quantity');
    minusBtn.disabled = recipient.quantity <= 1;
    minusBtn.textContent = '−';
    minusBtn.addEventListener('click', () => adjustQuantity(recipient.id, -1));

    const display = document.createElement('div');
    display.className = 'rcp-card__qty-display';
    // Inner wrapper so the number + unit share a baseline (baseline
    // alignment only acts between siblings in the same flex context);
    // the outer .rcp-card__qty-display still vertically centres this
    // group as a single unit.
    const text = document.createElement('span');
    text.className = 'rcp-card__qty-text';
    const num = document.createElement('span');
    num.className = 'rcp-card__qty-num';
    num.textContent = String(recipient.quantity);
    const unitEl = document.createElement('span');
    unitEl.className = 'rcp-card__qty-unit';
    unitEl.textContent = pluralize(recipient.quantity, unitSingular, unitPlural);
    text.append(num, unitEl);
    display.append(text);

    const plusBtn = document.createElement('button');
    plusBtn.type = 'button';
    plusBtn.className = 'rcp-card__qty-btn';
    plusBtn.setAttribute('aria-label', 'Increase quantity');
    plusBtn.disabled = atCapacity;
    plusBtn.textContent = '+';
    plusBtn.addEventListener('click', () => adjustQuantity(recipient.id, 1));

    qty.append(minusBtn, display, plusBtn);
    return qty;
  }

  function renderConfirmButtons(recipient) {
    const wrap = document.createElement('div');
    wrap.className = 'rcp-card__confirm';

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'rcp-card__confirm-btn';
    cancelBtn.disabled = isRemoving;
    cancelBtn.textContent = 'Cancel';
    cancelBtn.addEventListener('click', () => {
      confirmingRemoveId = null;
      render();
    });

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'rcp-card__confirm-btn';
    removeBtn.disabled = isRemoving;
    removeBtn.textContent = 'Remove';
    removeBtn.addEventListener('click', () => removeRecipient(recipient));

    wrap.append(cancelBtn, removeBtn);
    return wrap;
  }

  function adjustQuantity(id, delta) {
    const r = recipients.find(x => x.id === id);
    if (!r) return;
    const newQty = r.quantity + delta;
    if (newQty < 1) return;
    const otherTotal = recipients.reduce((s, x) => s + (x.id === id ? 0 : x.quantity), 0);
    if (otherTotal + newQty > capacity) return;
    r.quantity = newQty;
    render();
  }

  // Deletes immediately — no deferral to the dialog's own Confirm. Blocks
  // Cancel/Remove on this card and the dialog's Confirm until the call
  // resolves, so the on-screen list never gets ahead of the server: only
  // a successful delete drops the row and updates the allocation total
  // shown inside this dialog. The checkout page trigger itself doesn't
  // hear about it yet — that's deferred to Confirm or dialog-close (see
  // dispatchRecipientsUpdate below), not fired per removal. A recipient
  // added this session but never yet saved via Confirm (`persisted` still
  // false) has nothing to delete server-side, so that call is skipped
  // entirely.
  async function removeRecipient(recipient) {
    if (isRemoving) return;
    isRemoving = true;
    clearError();
    render();

    const saveUrl = activeOpener?.dataset.saveUrl;

    try {
      if (saveUrl && recipient.persisted) {
        await del(`${saveUrl}/${encodeURIComponent(recipient.addressId)}`);
      }
    } catch (err) {
      console.error('Remove recipient failed:', err);
      isRemoving = false;
      showError('Could not remove recipient. Please try again.');
      render();
      return;
    }

    recipients = recipients.filter(r => String(r.id) !== String(recipient.id));
    confirmingRemoveId = null;
    isRemoving = false;

    // Match by addressId, not id — persistedRecipients is a server-shaped
    // snapshot and the server response never carries the client's
    // synthetic row id (see addRecipient below), only addressId.
    if (recipient.persisted) {
      persistedRecipients = persistedRecipients.filter(r => r.addressId !== recipient.addressId);
    }

    if (!saveUrl) {
      // Dummy mode: keep the cache in step so the next open repopulates
      // without the removed recipient reappearing.
      const key = dummyKey(activeOpener);
      if (key) dummyRecipients.set(key, recipients.map(r => ({ ...r })));
    }

    render();
  }

  function addRecipient(address) {
    if (totalAllocated() >= capacity) return;

    // Dedup by addressId: if a recipient already references this address,
    // bump its quantity by one rather than creating a new row.
    const existing = recipients.find(r => r.addressId === address.id);
    if (existing) {
      existing.quantity += 1;
      render();
      return;
    }

    recipients.push({
      id:           `rcp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      addressId:    address.id,
      firstName:    address.firstName,
      lastName:     address.lastName,
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2,
      addressLine3: address.addressLine3,
      city:         address.city,
      postcode:     address.postcode,
      country:      address.country,
      quantity:     1,
      // Not yet saved server-side — stays false until a Confirm succeeds,
      // so removing it before that just drops it locally (see
      // removeRecipient above), no delete call needed.
      persisted:    false,
    });
    render();
  }

  function updateRecipientAddress(recipientId, address) {
    // recipientId comes from li.dataset.id, which DOMStringMap always
    // stringifies — recipients loaded from the server can have a non-string
    // (e.g. numeric) id in the array, so a strict === here would never match.
    const r = recipients.find(x => String(x.id) === String(recipientId));
    if (!r) return;

    // If the new address matches a different existing recipient, merge:
    // drop this row entirely and roll its quantity into the matched row
    // so the total allocation is preserved. Same string coercion as above —
    // without it, x.id !== recipientId is always true for a numeric id, so
    // the recipient being edited would falsely match itself as "other".
    const other = recipients.find(x => String(x.id) !== String(recipientId) && x.addressId === address.id);
    if (other) {
      other.quantity += r.quantity;
      recipients = recipients.filter(x => String(x.id) !== String(recipientId));
      render();
      return;
    }

    r.addressId    = address.id || r.addressId;
    r.firstName    = address.firstName;
    r.lastName     = address.lastName;
    r.addressLine1 = address.addressLine1;
    r.addressLine2 = address.addressLine2;
    r.addressLine3 = address.addressLine3;
    r.city         = address.city;
    r.postcode     = address.postcode;
    r.country      = address.country;
    render();
  }

  // Tells the checkout page trigger about persistedRecipients — never the
  // working `recipients` array, so an unconfirmed add/edit/quantity change
  // can't leak into what the trigger shows. Fired from onClose only —
  // covering both Confirm (which calls modalInstance.close() right after
  // updating persistedRecipients) and every other way the dialog can close
  // (X, Escape, backdrop) — so "X of Y allocated" updates exactly when the
  // customer presses Confirm or leaves the dialog, not on every individual
  // removal while it's still open.
  function dispatchRecipientsUpdate() {
    if (!activeOpener) return;
    activeOpener.dispatchEvent(new CustomEvent('recipients:confirm', {
      bubbles: true,
      detail: { recipients: persistedRecipients.map(r => ({ ...r })), count: persistedRecipients.length },
    }));
  }

  async function primeForOpener(opener) {
    activeOpener       = opener || null;
    recipients         = [];
    confirmingRemoveId = null;
    isRemoving         = false;
    capacity           = parseInt(opener?.dataset.quantity || '0', 10) || 0;
    unitSingular       = opener?.dataset.unitSingular || 'item';
    unitPlural         = opener?.dataset.unitPlural   || 'items';

    clearError();

    // Load order: real backend if a per-opener URL is rendered, otherwise
    // the in-memory dummy cache. Either source returns the enriched shape
    // (address fields + quantity per recipient), already persisted
    // server-side (or already reflected in the dummy cache).
    const loadUrl = opener?.dataset.loadUrl;
    if (loadUrl) {
      const saved = await get(loadUrl);
      if (Array.isArray(saved?.recipients)) {
        recipients = saved.recipients.map(r => ({ ...r, persisted: true }));
      }
    } else {
      const key = dummyKey(opener);
      if (key && dummyRecipients.has(key)) {
        recipients = dummyRecipients.get(key).map(r => ({ ...r }));
      }
    }

    // Whatever just loaded is, by definition, already persisted — this is
    // the server-truth baseline for this open, until a Confirm or delete
    // changes it (see dispatchRecipientsUpdate above).
    persistedRecipients = recipients.map(r => ({ ...r }));

    render();

    // Nothing to show yet — skip straight to the Add Recipient flow (which
    // itself skips straight to the address book, and on to the address
    // editor if the book is empty too) rather than showing an empty list.
    // Deferred a tick: in dummy mode this whole function resolves with no
    // real async gap, and modal.js's open() call for THIS dialog hasn't
    // reached its own showModal() yet — clicking addBtn synchronously here
    // would open the address book before the recipients dialog is marked
    // open, so it can't be obscured and both render stacked on top of
    // each other. Queuing the click lets that showModal() land first.
    if (recipients.length === 0) queueMicrotask(() => addBtn?.click());
  }

  const modalInstance = createModal(dialog, {
    closeOnBackdrop: false,
    onBeforeOpen: primeForOpener,
    onClose: () => {
      dispatchRecipientsUpdate();
      activeOpener = null;
      isSaving = false;
      confirmingRemoveId = null;
      isRemoving = false;
    },
  });

  // The "Add Recipient" button is reused for every recipient added, so we
  // need it to forget the previous address before each open, otherwise the
  // address book would preselect the last-added recipient's address. We
  // also stamp the remaining capacity onto the button so the address book
  // opens in multi-select mode with the right allocation cap, and the
  // addressIds already used by a recipient so the book can exclude them —
  // an address already picked here has nothing left to offer this flow.
  addBtn?.addEventListener('click', () => {
    delete addBtn.dataset.savedId;
    addBtn.dataset.remaining = String(capacity - totalAllocated());
    addBtn.dataset.excludeIds = JSON.stringify(recipients.map(r => r.addressId));
  }, true);

  // Edit flow: a card's top row opens the editor directly (above), and
  // address-form.js dispatches `address-form:confirm` on it once saved —
  // the ancestor card's id tells us which recipient to update. The Add
  // Recipient button is always multi-select (see data-remaining below), so
  // adding a recipient always arrives via address-book:multi-confirm
  // instead; the no-ancestor branch here is a defensive fallback, not a
  // path this flow actually takes.
  dialog.addEventListener('address-form:confirm', e => {
    const card = e.target.closest('.rcp-card');
    if (card?.dataset.id) updateRecipientAddress(card.dataset.id, e.detail);
    else                  addRecipient(e.detail);
  });

  // Multi-pick flow: the address book opened in multi-select mode (from the
  // Add Recipient button) dispatches this event on that same button once
  // Select is confirmed. Each picked/added address becomes a recipient at
  // quantity 1.
  dialog.addEventListener('address-book:multi-confirm', e => {
    const picked = Array.isArray(e.detail?.addresses) ? e.detail.addresses : [];
    picked.forEach(addr => addRecipient(addr));
  });

  // The address book closed (Select or cancel) — if it was the empty-
  // recipients bootstrap (primeForOpener's recipients.length === 0 branch
  // below) and nothing got added, this dialog is still just as pointless
  // as when it auto-forwarded to the book in the first place: close it
  // too, mirroring address-book.js's own empty-cancel cascade, so
  // cancelling all the way out lands back on the checkout page rather
  // than an empty recipients dialog. A successful add always pushes into
  // `recipients` (above) before the book closes, so this only ever fires
  // true on a genuine cancel.
  const addressBookDialog = document.getElementById('address-book');
  addressBookDialog?.addEventListener('close', () => {
    if (recipients.length === 0) modalInstance.close();
  });

  confirmBtn?.addEventListener('click', async () => {
    if (isSaving || isRemoving || recipients.length === 0) return;

    const saveUrl = activeOpener?.dataset.saveUrl;
    // POST contract: just addressId + quantity per recipient. The synthetic
    // row id and the cached address fields are client-side only — the server
    // already has the addresses and re-derives them when echoing the list back.
    const payload = {
      recipients: recipients.map(r => ({ addressId: r.addressId, quantity: r.quantity })),
    };

    let confirmedRecipients;
    if (saveUrl) {
      isSaving = true;
      confirmBtn.disabled = true;
      try {
        const saved = await post(saveUrl, payload);
        confirmedRecipients = (Array.isArray(saved?.recipients) ? saved.recipients : [])
          .map(r => ({ ...r, persisted: true }));
        confirmBtn.disabled = false;
      } catch (err) {
        console.error('Save recipients failed:', err);
        confirmBtn.disabled = false;
        isSaving = false;
        return;
      }

    } else {
      // Dummy mode: stash the locally-enriched copy so the next open repopulates
      // the cards without needing a server round-trip.
      confirmedRecipients = recipients.map(r => ({ ...r, persisted: true }));
      const key = dummyKey(activeOpener);
      if (key) dummyRecipients.set(key, confirmedRecipients);
    }
    isSaving = false;
    recipients = confirmedRecipients;
    persistedRecipients = confirmedRecipients.map(r => ({ ...r }));

    // No explicit dispatch here — modalInstance.close() below fires the
    // dialog's `close` event, and onClose (above) is the one place that
    // tells the checkout page trigger about the current list.
    modalInstance.close();
  });
}

function pluralize(n, singular, plural) {
  return n === 1 ? singular : plural;
}

function formatAddressLine({ addressLine1, addressLine2, addressLine3, city, postcode }) {
  return [addressLine1, addressLine2, addressLine3, city, postcode]
    .filter(s => s && s.trim())
    .join(', ');
}
